import { useModals } from '@customerio/pluma-components/react';
import type { PlumaManagedModalId } from '@customerio/pluma-components/react';
import { useBlocker } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { ConfirmLeaveModal } from './confirm-leave-modal';

type UseHistoryLeaveBlockerOptions = {
  /**
   * Read live (not through render state) so a navigation the guard already
   * resolved — it clears the pending edit before navigating — isn't re-blocked.
   */
  hasUnsavedChanges: () => boolean;
  /** Body copy for the modal; omit for its default "You have unsaved changes." */
  message?: string;
  /**
   * Omit to offer Discard / Go back only. To keep the action visible but
   * unavailable — a form that can save but not yet, e.g. failing validation —
   * pass it along with `isSaveDisabled` instead of omitting it.
   */
  onSave?: () => Promise<void> | void;
  /** Render "Save & Continue" disabled instead of hiding it — see the modal's prop. */
  isSaveDisabled?: boolean;
  onDiscard?: () => void;
};

/**
 * Prompts before an in-app history navigation (browser Back/Forward) abandons
 * unsaved edits, using the same `ConfirmLeaveModal` as `useNavigationGuard`.
 *
 * `useNavigationGuard` only hears `safeNavigate`-initiated transitions and
 * `beforeunload`, so popstate slips past it and silently discards pending edits —
 * a gap against the Ember pages being migrated, whose `routeWillChange` hook
 * caught Back/Forward too. Pair this with `useNavigationGuard` on any page that
 * holds unsaved state; teaching the guard itself to hear popstate (so pages get
 * this for free) is tracked separately.
 *
 * No double modal: a `safeNavigate` is handled by the guard, which clears the
 * pending state before navigating, so `hasUnsavedChanges()` is already false by
 * the time the blocker sees it. `enableBeforeUnload` stays off because the guard
 * owns the tab-close prompt.
 */
export function useHistoryLeaveBlocker({
  hasUnsavedChanges,
  message,
  onSave,
  isSaveDisabled,
  onDiscard,
}: UseHistoryLeaveBlockerOptions) {
  const modals = useModals();

  const blocker = useBlocker({
    shouldBlockFn: () => hasUnsavedChanges(),
    enableBeforeUnload: false,
    withResolver: true,
  });

  // Keep the latest resolver reachable from the modal's close handler.
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  const openModalIdRef = useRef<PlumaManagedModalId<typeof ConfirmLeaveModal> | null>(null);

  useEffect(() => {
    // Released only once the blocker has actually left 'blocked'. Clearing it when the
    // modal closes is too early: `proceed`/`reset` resolve a promise, so the status
    // lingers, and this effect re-runs on every render (the page hands it fresh
    // `onSave`/`onDiscard`/`message` identities) — a render in that gap would reopen the
    // prompt with an already-spent resolver.
    if (blocker.status !== 'blocked') {
      openModalIdRef.current = null;
      return;
    }
    // Open once per block; the ref also absorbs effect re-runs from dependency
    // identity changes while the modal is already open.
    if (openModalIdRef.current != null) {
      return;
    }

    const modalId = modals.open(
      ConfirmLeaveModal,
      { message, onSave, isSaveDisabled, onDiscard },
      { shouldCloseOnUnmount: true, withImmediateCallbacks: true },
    );
    openModalIdRef.current = modalId;

    void modals.waitForClose(modalId).then((result) => {
      // 'confirm' = discarded or saved successfully → let the navigation through;
      // anything else (Go back, Esc, overlay, failed save) cancels it.
      if (result.reason === 'confirm') {
        blockerRef.current.proceed?.();
      } else {
        blockerRef.current.reset?.();
      }
    });
  }, [blocker.status, modals, message, onSave, isSaveDisabled, onDiscard]);
}
